import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { requireAnyPermission } from '@/lib/permissions/permissions'
import { cn } from '@/lib/utils/ui'
import * as luxon from 'luxon'
import { useMemo, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleDashed,
  Clock,
  LayoutGrid,
  List,
  type LucideIcon,
} from 'lucide-react'
import { weeklyDueStatus } from '@/lib/checkins/dueStatus'
import { useOrg } from '@/lib/states/session/hooks'
import { useStore } from '@/lib/states/store'
import { trpc } from '@/lib/trpc/client'
import { Badge } from '@/components/ui/badge'

const lateBadgeClassName =
  'gap-1 bg-amber-100 text-amber-800 hover:bg-amber-200 [.dark_&]:bg-amber-950 [.dark_&]:text-amber-300 [.dark_&]:hover:bg-amber-900'

export const Route = createFileRoute('/_auth/_dashboard/checkins')({
  beforeLoad: () => {
    requireAnyPermission(['clients.read_all', 'clients.read_assigned'])
  },
  component: CheckinsPage,
})

// ─── Data types ──────────────────────────────────────────────────────────────

type Assignment = {
  form_id: string
  client_id: string
  assign_date: string
  next_date: string | null
  reminder_days: number[] | null
  reminder_time: string | null
  check_in_type: string | null
  check_in_day: number | null
  client: {
    id: string
    org_id: string
    timezone: string
    status: string
    first_name: string
    last_name: string
    avatar: { path: string; url: string } | null
  } | null
  form: {
    id: string
    name: string | null
    type: string | null
  } | null
}

type Response = {
  id: string
  response_date: string
  reviewed: boolean | null
  client_id: string
  form_id: string
  coach_res: {
    created_at: string
  }[]
}

// ─── Row shape shared across all three buckets ────────────────────────────────

type CheckInBucket = 'due' | 'waiting' | 'reviewed'

type CheckInRow = {
  key: string
  bucket: CheckInBucket
  clientId: string
  clientName: string
  clientInitials: string
  avatarUrl?: string
  formId: string
  formName: string
  timezone: string
  // Only set for due rows
  nextDate: string | null
  missed: boolean
  // The most recent response for this client:form, regardless of bucket.
  // Due rows use this for the "Last response" display; waiting/reviewed rows use `response` for navigation.
  response: Response | null
  lastResponseDate: string | null
  reviewDate: string | null
}

// ─── Section types ────────────────────────────────────────────────────────────

type CheckInFilter = 'all' | CheckInBucket
type CheckInSectionKey = CheckInBucket

type CheckInSection = {
  key: CheckInSectionKey
  title: string
  icon: LucideIcon
  iconClassName: string
  items: CheckInRow[]
  emptyText: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const makeRowBase = (
  assignment: Assignment,
): Pick<
  CheckInRow,
  | 'clientId'
  | 'clientName'
  | 'clientInitials'
  | 'avatarUrl'
  | 'formId'
  | 'formName'
  | 'timezone'
> => {
  const clientName = `${assignment.client?.first_name ?? ''} ${
    assignment.client?.last_name ?? ''
  }`.trim()
  const clientInitials = `${assignment.client?.first_name?.[0] ?? ''}${
    assignment.client?.last_name?.[0] ?? ''
  }`.trim()
  return {
    clientId: assignment.client_id,
    clientName: clientName || 'Unnamed client',
    clientInitials: clientInitials || 'CL',
    avatarUrl: assignment.client?.avatar?.url,
    formId: assignment.form_id,
    formName: assignment.form?.name ?? 'Check-in',
    timezone: assignment.client?.timezone ?? 'UTC',
  }
}

// ─── Route ────────────────────────────────────────────────────────────────────

function CheckinsPage() {
  const org = useOrg()
  const navigate = Route.useNavigate()
  const displayMode = useStore((state) => state.checkins.displayMode)
  const setDisplayMode = useStore((state) => state.checkins.setDisplayMode)
  const [activeFilter, setActiveFilter] = useState<CheckInFilter>('all')
  const [displayPopoverOpen, setDisplayPopoverOpen] = useState(false)
  const [openSections, setOpenSections] = useState<
    Record<CheckInSectionKey, boolean>
  >({
    due: true,
    waiting: true,
    reviewed: true,
  })

  // ── Inbox query (assignments + recent responses) ───────────────────────────
  const { data: inbox, isLoading: isInboxLoading } =
    trpc.checkins.inbox.useQuery(undefined, {
      enabled: Boolean(org?.id),
    })
  const assignments = inbox?.assignments
  const responses = inbox?.responses

  const assignmentKeys = useMemo(() => {
    const keys = new Set<string>()
    ;(assignments ?? []).forEach((a) => {
      if (a.client_id && a.form_id) {
        keys.add(`${a.client_id}:${a.form_id}`)
      }
    })
    return keys
  }, [assignments])

  // ── Helpers to get client display info from assignment ─────────────────────
  const assignmentByKey = useMemo(() => {
    const map = new Map<string, Assignment>()
    ;(assignments ?? []).forEach((a) => {
      map.set(`${a.client_id}:${a.form_id}`, a)
    })
    return map
  }, [assignments])

  // ── "Due" bucket ───────────────────────────────────────────────────────────
  // An assignment is "due" when the client has no response whose response_date
  // falls in the current ISO Mon–Sun week (in the client's timezone).
  const dueItems = useMemo<CheckInRow[]>(() => {
    const items: CheckInRow[] = []

    ;(assignments ?? [])
      .filter((a) => a.form && a.client)
      .forEach((assignment) => {
        const timezone = assignment.client?.timezone ?? 'UTC'
        const now = luxon.DateTime.now().setZone(timezone)
        const weekStart = now.startOf('week')
        const weekEnd = now.endOf('week')

        const hasResponseThisWeek = (responses ?? []).some((r) => {
          if (
            r.client_id !== assignment.client_id ||
            r.form_id !== assignment.form_id
          ) {
            return false
          }
          const rd = luxon.DateTime.fromISO(r.response_date).setZone(timezone)
          return rd >= weekStart && rd <= weekEnd
        })

        if (hasResponseThisWeek) return

        // Find the most recent response for display (responses are ordered desc)
        const latestResponse =
          (responses ?? []).find(
            (r) =>
              r.client_id === assignment.client_id &&
              r.form_id === assignment.form_id,
          ) ?? null

        const dueStatus = weeklyDueStatus({
          checkInDay: assignment.check_in_day,
          reminderDays: assignment.reminder_days,
          timezone,
          lastResponseAt: latestResponse?.response_date ?? null,
          now,
        })

        items.push({
          key: `due:${assignment.client_id}:${assignment.form_id}`,
          bucket: 'due',
          ...makeRowBase(assignment),
          nextDate: dueStatus.dueDate?.toISO() ?? assignment.next_date,
          missed: dueStatus.missed,
          response: null,
          lastResponseDate: latestResponse?.response_date ?? null,
          reviewDate: null,
        })
      })

    // Sort: next_date ascending, nulls last
    return items.sort((a, b) => {
      if (!a.nextDate && !b.nextDate) return 0
      if (!a.nextDate) return 1
      if (!b.nextDate) return -1
      return a.nextDate.localeCompare(b.nextDate)
    })
  }, [assignments, responses])

  // ── "Waiting" bucket ───────────────────────────────────────────────────────
  // Every unreviewed response in the last 28 days from a weekly check-in.
  // One row per response; client can appear multiple times.
  // Sorted oldest-first (most overdue at the top).
  const waitingItems = useMemo<CheckInRow[]>(() => {
    const items: CheckInRow[] = []

    ;(responses ?? [])
      .filter(
        (r) =>
          r.reviewed === false &&
          assignmentKeys.has(`${r.client_id}:${r.form_id}`),
      )
      .forEach((r) => {
        const assignment = assignmentByKey.get(`${r.client_id}:${r.form_id}`)
        if (!assignment?.client || !assignment?.form) return

        items.push({
          key: `waiting:${r.id}`,
          bucket: 'waiting',
          ...makeRowBase(assignment),
          nextDate: assignment.next_date,
          missed: false,
          response: r,
          lastResponseDate: r.response_date,
          reviewDate: null,
        })
      })

    // Oldest unreviewed first
    return items.sort((a, b) => {
      const aDate = a.response?.response_date ?? ''
      const bDate = b.response?.response_date ?? ''
      return aDate.localeCompare(bDate)
    })
  }, [responses, assignmentKeys, assignmentByKey])

  // ── "Reviewed" bucket ──────────────────────────────────────────────────────
  // Responses that are reviewed=true AND were reviewed in the current
  // ISO Mon–Sun week (per client timezone). Most recent reviews first.
  const reviewedItems = useMemo<CheckInRow[]>(() => {
    const items: CheckInRow[] = []

    ;(responses ?? [])
      .filter(
        (r) =>
          r.reviewed === true &&
          assignmentKeys.has(`${r.client_id}:${r.form_id}`),
      )
      .forEach((r) => {
        const assignment = assignmentByKey.get(`${r.client_id}:${r.form_id}`)
        if (!assignment?.client || !assignment?.form) return

        const timezone = assignment.client?.timezone ?? 'UTC'
        const now = luxon.DateTime.now().setZone(timezone)
        const reviewDate =
          r.coach_res
            ?.map((review) => review.created_at)
            .sort((a, b) => b.localeCompare(a))[0] ?? null
        if (!reviewDate) return

        const reviewedAt = luxon.DateTime.fromISO(reviewDate).setZone(timezone)
        if (reviewedAt < now.startOf('week') || reviewedAt > now.endOf('week')) {
          return
        }

        items.push({
          key: `reviewed:${r.id}`,
          bucket: 'reviewed',
          ...makeRowBase(assignment),
          nextDate: assignment.next_date,
          missed: false,
          response: r,
          lastResponseDate: r.response_date,
          reviewDate,
        })
      })

    // Most recent first
    return items.sort((a, b) => {
      const aDate = a.reviewDate ?? ''
      const bDate = b.reviewDate ?? ''
      return bDate.localeCompare(aDate)
    })
  }, [responses, assignmentKeys, assignmentByKey])

  // ── Sections ───────────────────────────────────────────────────────────────

  const sections = useMemo<CheckInSection[]>(
    () => [
      {
        key: 'due',
        title: 'Check-in Due',
        icon: CircleDashed,
        iconClassName: 'text-amber-500',
        items: dueItems,
        emptyText: 'No check-ins due.',
      },
      {
        key: 'waiting',
        title: 'Waiting for Review',
        icon: Circle,
        iconClassName: 'text-blue-500',
        items: waitingItems,
        emptyText: 'Nothing waiting for review.',
      },
      {
        key: 'reviewed',
        title: 'Reviewed',
        icon: CheckCircle2,
        iconClassName: 'text-emerald-500',
        items: reviewedItems,
        emptyText: 'No reviewed check-ins yet.',
      },
    ],
    [dueItems, waitingItems, reviewedItems],
  )

  const isLoading = Boolean(isInboxLoading)
  const isFilteredView = activeFilter !== 'all'
  const isBoardDisplay = !isFilteredView && displayMode === 'board'
  const visibleSections = useMemo(
    () =>
      activeFilter === 'all'
        ? sections
        : sections.filter((section) => section.key === activeFilter),
    [activeFilter, sections],
  )

  const handleRowNavigate = (item: CheckInRow) => {
    if (
      (item.bucket === 'waiting' || item.bucket === 'reviewed') &&
      item.response
    ) {
      void navigate({
        to: '/clients/$clientId/checkin/$formId/$responseId',
        params: {
          clientId: item.clientId,
          formId: item.response.form_id,
          responseId: item.response.id.toString(),
        },
      })
      return
    }

    void navigate({
      to: '/clients/$clientId/checkin',
      params: { clientId: item.clientId },
    })
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="px-4 pt-6 md:px-8 md:pt-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-3xl font-bold tracking-tight">Check-ins</h2>
          <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto sm:items-center">
            <Select
              value={activeFilter}
              onValueChange={(value) => setActiveFilter(value as CheckInFilter)}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All check-ins" />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="all">All check-ins</SelectItem>
                <SelectItem value="due">Due</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
              </SelectContent>
            </Select>
            <Popover
              open={displayPopoverOpen}
              onOpenChange={setDisplayPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full gap-2 px-4 sm:w-auto"
                  aria-label="Display options"
                >
                  {isBoardDisplay ? (
                    <LayoutGrid className="h-4 w-4" />
                  ) : (
                    <List className="h-4 w-4" />
                  )}
                  Display
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[220px] rounded-xl p-2">
                <div className="flex gap-2">
                  <DisplayOptionButton
                    label="List"
                    icon={List}
                    isActive={!isBoardDisplay}
                    onClick={() => {
                      setDisplayMode('list')
                      setDisplayPopoverOpen(false)
                    }}
                  />
                  <DisplayOptionButton
                    label="Board"
                    icon={LayoutGrid}
                    isActive={isBoardDisplay}
                    onClick={() => {
                      setDisplayMode('board')
                      setDisplayPopoverOpen(false)
                    }}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      <TooltipProvider>
        {isBoardDisplay ? (
          <div className="min-h-0 flex-1">
            <div className="h-full overflow-x-auto overflow-y-hidden pb-2">
              <div className="flex h-full min-w-max gap-3 pl-4 md:pl-8">
                {visibleSections.map((column) => (
                  <CheckInColumn
                    key={column.key}
                    title={column.title}
                    icon={column.icon}
                    iconClassName={column.iconClassName}
                    items={column.items}
                    isLoading={isLoading}
                    emptyText={column.emptyText}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <CheckInListView
            sections={visibleSections}
            isLoading={isLoading}
            openSections={openSections}
            onRowNavigate={handleRowNavigate}
            onOpenChange={(key, open) =>
              setOpenSections((current) => ({ ...current, [key]: open }))
            }
          />
        )}
      </TooltipProvider>
    </div>
  )
}

// ─── UI components ─────────────────────────────────────────────────────────

type ColumnProps = {
  title: string
  icon: LucideIcon
  iconClassName: string
  items: CheckInRow[]
  isLoading: boolean
  emptyText: string
}

type DisplayOptionButtonProps = {
  label: string
  icon: LucideIcon
  isActive: boolean
  onClick: () => void
}

const DisplayOptionButton = ({
  label,
  icon: Icon,
  isActive,
  onClick,
}: DisplayOptionButtonProps) => (
  <Button
    type="button"
    variant="ghost"
    onClick={onClick}
    className={cn(
      'h-9 flex-1 justify-center rounded-md border border-border bg-background px-3 text-sm font-medium shadow-sm',
      isActive ? 'bg-secondary' : 'hover:bg-secondary/60',
    )}
  >
    <Icon className="mr-2 h-4 w-4" />
    {label}
  </Button>
)

type CheckInListViewProps = {
  sections: CheckInSection[]
  isLoading: boolean
  openSections: Record<CheckInSectionKey, boolean>
  onRowNavigate: (item: CheckInRow) => void
  onOpenChange: (key: CheckInSectionKey, open: boolean) => void
}

const CheckInListView = ({
  sections,
  isLoading,
  openSections,
  onRowNavigate,
  onOpenChange,
}: CheckInListViewProps) => (
  <div className="min-h-0 flex-1 overflow-y-auto pb-4 md:pb-8">
    <div className="px-4 md:px-8">
      <div className="w-full">
        {sections.map((section) => (
          <section key={section.key} className="w-full">
            <div className="sticky top-0 z-20 bg-background">
              <button
                type="button"
                onClick={() =>
                  onOpenChange(section.key, !openSections[section.key])
                }
                className="flex w-full items-center justify-between gap-3 rounded-xl bg-secondary/70 px-4 py-3 text-left backdrop-blur supports-[backdrop-filter]:bg-secondary/60"
              >
                <div className="flex items-center gap-2">
                  {openSections[section.key] ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <section.icon
                    className={cn('h-4 w-4 shrink-0', section.iconClassName)}
                  />
                  <span className="text-sm font-semibold">{section.title}</span>
                  <span className="text-sm text-muted-foreground">
                    {section.items.length}
                  </span>
                </div>
              </button>
            </div>
            {openSections[section.key] &&
              (isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={`${section.key}-skeleton-${i}`}
                    className="rounded-xl bg-background px-4 py-4"
                  >
                    <div className="h-8 animate-pulse rounded-md bg-muted" />
                  </div>
                ))
              ) : section.items.length === 0 ? (
                <div className="rounded-xl bg-background px-4 py-4 text-sm text-muted-foreground">
                  {section.emptyText}
                </div>
              ) : (
                section.items.map((item) => (
                  <CheckInListRow
                    key={item.key}
                    item={item}
                    onNavigate={onRowNavigate}
                    sectionIcon={section.icon}
                    sectionIconClassName={section.iconClassName}
                  />
                ))
              ))}
          </section>
        ))}
      </div>
    </div>
  </div>
)

const CheckInColumn = ({
  title,
  icon: Icon,
  iconClassName,
  items,
  isLoading,
  emptyText,
}: ColumnProps) => (
  <section className="flex h-full min-h-0 w-[320px] shrink-0 flex-col overflow-hidden rounded-2xl bg-secondary/70 sm:w-[340px]">
    <div className="flex items-center gap-2 px-4 pb-3 pt-3">
      <Icon className={`h-4 w-4 shrink-0 ${iconClassName}`} />
      <h3 className="text-sm font-semibold">{title}</h3>
      <span className="text-sm text-muted-foreground">{items.length}</span>
    </div>
    <ScrollArea className="min-h-0 flex-1">
      {isLoading ? (
        <div className="space-y-2 px-3 pb-3 pr-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl bg-background/70"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="px-3 pb-3 pr-2">
          <div className="rounded-xl bg-background/80 p-4 text-sm text-muted-foreground">
            {emptyText}
          </div>
        </div>
      ) : (
        <div className="space-y-2.5 px-3 pb-3">
          {items.map((item) => (
            <CheckInCard key={item.key} item={item} />
          ))}
        </div>
      )}
    </ScrollArea>
  </section>
)

const formatCheckInDate = (date: string | null, timezone: string) => {
  if (!date) {
    return 'No date'
  }

  const dateTime = luxon.DateTime.fromISO(date).setZone(timezone)
  const today = luxon.DateTime.now().setZone(timezone).startOf('day')
  const targetDay = dateTime.startOf('day')
  const diffDays = Math.round(targetDay.diff(today, 'days').days)

  if (diffDays === 0) {
    return 'Today'
  }

  if (diffDays === -1) {
    return 'Yesterday'
  }

  if (diffDays === 1) {
    return 'Tomorrow'
  }

  return dateTime.toFormat('d MMM')
}

const formatRelativeCheckInDate = (date: string | null, timezone: string) => {
  if (!date) {
    return 'Unknown'
  }

  const dateTime = luxon.DateTime.fromISO(date).setZone(timezone)
  const now = luxon.DateTime.now().setZone(timezone)

  if (dateTime.hasSame(now, 'day')) {
    return 'Today'
  }

  if (dateTime.hasSame(now.minus({ days: 1 }), 'day')) {
    return 'Yesterday'
  }

  return (
    dateTime.toRelative({
      style: 'long',
    }) ?? dateTime.toFormat('d MMM')
  )
}

const CheckInListRow = ({
  item,
  onNavigate,
  sectionIcon: SectionIcon,
  sectionIconClassName,
}: {
  item: CheckInRow
  onNavigate: (item: CheckInRow) => void
  sectionIcon: LucideIcon
  sectionIconClassName: string
}) => {
  const displayDate =
    item.bucket === 'due'
      ? formatCheckInDate(item.nextDate, item.timezone)
      : item.bucket === 'reviewed'
        ? formatCheckInDate(item.reviewDate, item.timezone)
        : formatCheckInDate(item.lastResponseDate, item.timezone)

  return (
    <div
      className={cn(
        'w-full rounded-xl bg-background',
        'cursor-pointer hover:bg-secondary/40',
      )}
      onClick={() => onNavigate(item)}
    >
      <div className="flex w-full items-center gap-3 px-4 py-3">
        <Avatar className="h-9 w-9 shrink-0 rounded-md">
          <AvatarImage src={item.avatarUrl} className="object-cover" />
          <AvatarFallback className="rounded-md text-xs font-semibold">
            {item.clientInitials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-3">
            <span className="truncate text-sm font-medium">
              {item.clientName}
            </span>
            <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
              <SectionIcon
                className={cn('h-4 w-4 shrink-0', sectionIconClassName)}
              />
              <span className="truncate">{item.formName}</span>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 text-right md:w-[180px]">
          {item.missed ? (
            <Badge variant="secondary" className={lateBadgeClassName}>
              <Clock className="h-3 w-3" />
              Late
            </Badge>
          ) : null}
          <div className="text-sm text-muted-foreground">{displayDate}</div>
        </div>
      </div>
    </div>
  )
}

const CheckInCard = ({ item }: { item: CheckInRow }) => {
  const responseDate = item.lastResponseDate
    ? formatCheckInDate(item.lastResponseDate, item.timezone)
    : 'No response yet'

  const dueDate = item.nextDate
    ? formatCheckInDate(item.nextDate, item.timezone)
    : 'No schedule'

  const submittedRelativeDate = formatRelativeCheckInDate(
    item.lastResponseDate,
    item.timezone,
  )
  const reviewedRelativeDate = formatRelativeCheckInDate(
    item.reviewDate,
    item.timezone,
  )

  const cardBody = (
    <div
      className={cn(
        'block rounded-xl border border-border/60 bg-background p-3 transition',
        'hover:border-primary/30 hover:bg-background/95 hover:shadow-sm',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-md">
            <AvatarImage src={item.avatarUrl} className="object-cover" />
            <AvatarFallback className="rounded-md text-xs font-semibold">
              {item.clientInitials}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-1">
            <div className="text-sm font-semibold">{item.clientName}</div>
            <div className="text-xs text-muted-foreground">{item.formName}</div>
          </div>
        </div>
        {item.missed ? (
          <Badge variant="secondary" className={lateBadgeClassName}>
            <Clock className="h-3.5 w-3.5" /> Late
          </Badge>
        ) : item.bucket === 'waiting' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="rounded-full bg-blue-100 p-1 text-blue-700">
                <Circle className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="left">Waiting for review</TooltipContent>
          </Tooltip>
        ) : item.bucket === 'reviewed' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="rounded-full bg-green-100 p-1 text-green-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="left">Reviewed</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      {item.bucket === 'waiting' ? (
        <div className="mt-2 text-xs text-muted-foreground">
          Submitted {submittedRelativeDate}
        </div>
      ) : item.bucket === 'reviewed' ? (
        <>
          <div className="mt-2 text-xs text-muted-foreground">
            Reviewed {reviewedRelativeDate}
          </div>
          <div className="text-xs text-muted-foreground">
            Submitted {submittedRelativeDate}
          </div>
        </>
      ) : (
        <>
          <div className="mt-2 text-xs text-muted-foreground">
            Due: {dueDate}
          </div>
          <div className="text-xs text-muted-foreground">
            Last response: {responseDate}
          </div>
        </>
      )}
    </div>
  )

  if (
    (item.bucket === 'waiting' || item.bucket === 'reviewed') &&
    item.response
  ) {
    return (
      <Link
        to="/clients/$clientId/checkin/$formId/$responseId"
        params={{
          clientId: item.clientId,
          formId: item.response.form_id,
          responseId: item.response.id.toString(),
        }}
        className="block"
      >
        {cardBody}
      </Link>
    )
  }

  return (
    <Link
      to="/clients/$clientId/checkin"
      params={{ clientId: item.clientId }}
      className="block"
    >
      {cardBody}
    </Link>
  )
}
